import React from 'react';
import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useModal } from 'mui-modal-provider';
import { useNavigate } from 'react-router-dom';

import CounterButton from '../../../DesignSystem/CounterButton';
import usePostVote from '../../../../Hooks/usePostVote';
import useSpace from '../../../../Hooks/useSpace';
import useCurrentUser from '../../../../Hooks/useCurrentUser';
import { VoteTypes, PostStatuses, PostTypes } from '../../../../Utils/Types';
import { getVoteNameByPostType } from '../../../../Utils/Post/votes';
import { loginPath } from '../../../../Utils/routes';
import EntitySubscriptionModal from '../../../EntitySubscriptionModal';

const votableStatuses = [
  PostStatuses.SELECTED,
  PostStatuses.VALIDATED,
  PostStatuses.IN_PROGRESS,
  PostStatuses.DONE,
];

function VoteButton({
  reaction,
  post,
  isResponsive = false,
  showLabel = true,
  onClickVote = () => {},
  trackingProps,
  ...props
}: any) {
  const { vote, selectedVote, loading: voting } = usePostVote(post, trackingProps);
  const parent = post.included.source;
  const { space } = useSpace();
  const navigate = useNavigate();
  const { showModal } = useModal();

  const { isLoggedIn } = useCurrentUser();
  const { type } = parent;
  const canCreateVote = post.permissions.can_create_vote;
  const hasReachMaxVotes =
    !!parent.voting_options.max_votes &&
    parent.voting_options.max_votes <= parent.meta?.user_votes_info?.total_votes;
  const noBudgetLeft =
    parent?.voting_options?.budget_rule &&
    !parent.meta?.user_votes_info?.budget_left <= post.budget;
  const hasVotableStatus =
    votableStatuses.includes(post.status) || PostTypes.IDEAS_REQUEST === type;
  // we allow non logged in users to click on the vote button
  // then we take them to the login page
  const voteDisabled = isLoggedIn && (!canCreateVote || !hasVotableStatus);

  const onClick = async (event: any) => {
    event.preventDefault();

    if (!isLoggedIn) {
      navigate(loginPath());
    } else {
      if (!space.entity_subscription) {
        showModal(EntitySubscriptionModal);
      } else {
        // Checks for secured strategies are done in the usePostVote hook
        // and handles redirect to the secured strategies page
        await vote(reaction);
        onClickVote();
      }
    }

    return false;
  };

  let tooltipLabel;

  if (hasReachMaxVotes) {
    tooltipLabel = <FormattedMessage id="USER_VOTES_INFO.MAX_VOTES_ERROR" />;
  } else if (noBudgetLeft) {
    tooltipLabel = <FormattedMessage id="POSTS.BUDGET.NOT_ENOUGH_BUDGET" />;
  }

  return (
    <Tooltip arrow disableHoverListener={!voteDisabled} title={tooltipLabel}>
      <Box>
        <CounterButton
          fullWidth={isResponsive}
          reaction={reaction}
          active={reaction === selectedVote}
          onClick={onClick}
          onMouseDown={(event: any) => event.stopPropagation()}
          isFetching={voting}
          data-testid="vote-button"
          disabled={voteDisabled}
          showLabel={showLabel}
          {...props}
        >
          <FormattedMessage id={`BUTTONS.VOTE.${getVoteNameByPostType(parent.type)[reaction]}`} />
        </CounterButton>
      </Box>
    </Tooltip>
  );
}

VoteButton.propTypes = {
  reaction: PropTypes.oneOf(Object.values(VoteTypes)).isRequired,
  isResponsive: PropTypes.bool,
  post: PropTypes.object,
  onClickVote: PropTypes.func,
  trackingProps: PropTypes.object,
  showLabel: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default VoteButton;
